import { executeGraphQlQuery } from '../helpers/storefront-graphql-helper'
import { getGraphQLIds, getGraphQLID } from './shopify'
import { normalizeProducts } from './shopify-normalize'

const normalizeGqlProductFields = () => `
  title
  id
  handle
  vendor
  onlineStoreUrl
  availableForSale
  featuredImage {
    altText
    url
  }
  images(first: 10) {
    nodes {
      url
      altText
    }
  }
  collections(first: 10) {
    nodes {
      handle
    }
  }
  media(first: 10) {
    nodes {
      ... on Video {
        id
        mediaContentType
        sources {
          url
          format
          mimeType
        }
      }
      ... on ExternalVideo {
        id
        mediaContentType
        host
        embedUrl
      }
      ... on MediaImage {
        id
        mediaContentType
        alt
        image {
          url
        }
      }
    }
  }
  priceRange {
    minVariantPrice {
      amount
      currencyCode
    }
  }
  compareAtPriceRange {
    minVariantPrice {
      amount
      currencyCode
    }
  }
  variants(first: 50) {
    nodes {
      id
      title
      quantityAvailable
      availableForSale
      price {
        amount
      }
      compareAtPrice {
        amount
      }
      selectedOptions {
        value
      },
      image {
        altText
        url
      }
    }
  }
  productType
  tags
  options(first: 5) {
    name
    values
  }
  metafields(identifiers: [{namespace: "custom", key: "product_badge_award"}]) {
    key
    namespace
    value
    reference {
      ... on Metaobject {
        fields {
          key
          value
        }
      }
    }
  }
`

const buildProductsQuery = (ids) => {
  const variables = { ids }

  const query = `
    query Nodes($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on Product {
          ${normalizeGqlProductFields()}
        }
      }
    }
  `

  return {
    query,
    variables
  }
}

const buildCollectionsQuery = (handle, filters, size, after) => {
  const variables = {
    handle,
    filters,
    first: size,
    after
  }

  const query = `
  query Collection($handle: String, $first: Int, $filters: [ProductFilter!], $after: String) {
    collection(handle: $handle) {
      products(first: $first, filters: $filters, after: $after) {
        pageInfo {
          hasNextPage
          endCursor
          startCursor
          hasPreviousPage
        }
        nodes {
          ${normalizeGqlProductFields()}
        }
      }
    }
  }
  `

  return {
    query,
    variables
  }
}

const buildRecommendationsQuery = (id) => {
  const variables = { id }

  const query = `
    query ProductRecommendations($id: ID!) {
      productRecommendations(productId: $id) {
        ${normalizeGqlProductFields()}
      }
    }
  `

  return {
    query,
    variables
  }
}

const buildQueryNewestArticles = (limit = 1) => {
  const variables = { limit }
  const query = `
    query Articles($limit: Int) {
      articles(first: $limit, reverse: true, sortKey: PUBLISHED_AT) {
        edges {
          node {
            title
            handle
            onlineStoreUrl
            image {
              url
            }
            blog {
              title
            }
            tags
          }
        }
      }
    }
  `

  return {
    query,
    variables
  }
}

const fetchProductByCollectionHandle = async (handle, filters = [], size = 10, after = null) => {
  const gqlQuery = buildCollectionsQuery(handle, filters, size, after)
  const productsNodes = executeGraphQlQuery(gqlQuery)
    .then((res) => {
      if (!res.ok) throw res

      return res.json()
    }).then((body) => body.data.collection.products)

  return productsNodes
}

const fetchProductByIds = async (ids, typeNormalize = '') => {
  const gids = getGraphQLIds(ids, 'Product')
  const gqlQuery = buildProductsQuery(gids)
  const products = executeGraphQlQuery(gqlQuery)
    .then((res) => {
      if (!res.ok) throw res

      return res.json()
    }).then((body) => typeNormalize ? normalizeProducts(body.data.nodes, typeNormalize) : body.data.nodes)

  return products
}

const fetchRecommendedProductById = async (id) => {
  const gid = getGraphQLID(id, 'Product')
  const gqlQuery = buildRecommendationsQuery(gid)
  const products = executeGraphQlQuery(gqlQuery)
    .then((res) => {
      if (!res.ok) throw res

      return res.json()
    }).then((body) => body.data.productRecommendations)

  return products
}

const buildMetaobjectByType = (type) => {
  const variables = { type }
  const query = `
    query MetaobjectByTypeQuery($type: String!) {
      metaobjects(type: $type, first: 100) {
        nodes {
          fields {
            key
            value
            reference {
              ... on MediaImage {
                image {
                  url
                }
              }
            }
          }
        }
      }
    }
  `

  return {
    query,
    variables: { ...variables, type }
  }
}

const fetchMetaobjectByType = async (type) => {
  const gqlQuery = buildMetaobjectByType(type)
  const metaobjects = executeGraphQlQuery(gqlQuery)
    .then((res) => {
      if (!res.ok) throw res

      return res.json()
    }).then((body) => body.data.metaobjects.nodes)

  return metaobjects
}

const fetchMetaobjectByGid = async (id) => {
  const query = `
  {
    metaobject(id: "${id}") {
      fields {
        value
        type
        key
      }
    }
  }
  `
  const metaobject = await executeGraphQlQuery({ query })
    .then((res) => {
      if (!res.ok) throw res

      return res.json()
    })
    .then((body) => {
      return body?.data
    })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.error('Could not fetch metaobject by gid', err)

      if (err.errors) {
        return err
      }

      return null
    })

  return metaobject
}

const fetchNewestArticles = async (limit) => {
  const gqlQuery = buildQueryNewestArticles(limit)
  const articles = executeGraphQlQuery(gqlQuery)
    .then((res) => {
      if (!res.ok) throw res

      return res.json()
    }).then((body) => body.data.articles.edges)

  return articles
}

const buildMetaobjectQuery = (ids) => {
  const variables = { ids }

  const query = `
    query Nodes($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on Metaobject {
          id
          fields {
            key
            reference {
              ... on MediaImage {
                image {
                  src
                  originalSrc
                }
              }
            }
            value
          }
        }
      }
    }
  `

  return {
    query,
    variables
  }
}

const fetchMetaobjectByGids = async (ids) => {
  const gids = getGraphQLIds(ids, 'Metaobject')
  const gqlQuery = buildMetaobjectQuery(gids)
  const metaobjects = executeGraphQlQuery(gqlQuery)
    .then((res) => {
      if (!res.ok) throw res

      return res.json()
    }).then((body) => body.data.nodes)

  return metaobjects
}

const buildProductQuery = (ids) => {
  const variables = { ids }

  const query = `
    query Nodes($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on Product {
          id
          media(first: 2) {
            nodes {
              ... on Video {
                id
                mediaContentType
                sources {
                  url
                  format
                }
              }
              ... on ExternalVideo {
                id
                mediaContentType
                host
                embedUrl
              }
              ... on MediaImage {
                mediaContentType
                image {
                  url
                }
              }
            }
          }
        }
      }
    }
  `

  return {
    query,
    variables
  }
}

const fetchProductByIDs = async (ids) => {
  const gids = getGraphQLIds(ids, 'Product')
  const gqlQuery = buildProductQuery(gids)
  const products = executeGraphQlQuery(gqlQuery)
    .then((res) => {
      if (!res.ok) throw res

      return res.json()
    }).then((body) => body.data.nodes)

  return products
}

export {
  fetchProductByCollectionHandle,
  fetchRecommendedProductById,
  fetchMetaobjectByType,
  fetchMetaobjectByGid,
  fetchProductByIds,
  fetchNewestArticles,
  normalizeGqlProductFields,
  fetchMetaobjectByGids,
  fetchProductByIDs
}
